import React, { useState, useEffect, useCallback } from 'react';
import { BrowserRouter, Navigate, Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import { AppBar, Box, Container, CssBaseline, Toolbar, Typography } from '@mui/material';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import ChatWidget from './components/ChatWidget';
import ProductChatWidget from './components/ProductChatWidget';
import LoginPage from './components/LoginPage';
import SetPasswordPage from './components/SetPasswordPage';
import AdminDashboard from './components/AdminDashboard';
import { loadToken, setAuthToken, setAuthFailureHandler } from './api';

function AppContent({ auth, onLogin, onLogout }) {
  const location = useLocation();
  const navigate = useNavigate();

  const handleSessionExpired = useCallback(() => {
    onLogout();
    navigate('/admin/dashboard/overview', { replace: true });
  }, [navigate, onLogout]);

  useEffect(() => {
    setAuthFailureHandler(handleSessionExpired);
    return () => setAuthFailureHandler(() => {});
  }, [handleSessionExpired]);
  const isAdminRoute = location.pathname.startsWith("/admin");
  const isEmbedRoute = location.pathname === "/embed";
  const isProductEmbedRoute = location.pathname === "/product-embed";

  if (isEmbedRoute) {
    return <ChatWidget mode="public" />;
  }

  if (isProductEmbedRoute) {
    return <ProductChatWidget />;
  }

  return (
    <>
      {isAdminRoute && auth && (
        <AppBar position="sticky" elevation={0}>
          <Toolbar>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, flexGrow: 1 }}>
              <Box
                sx={{
                  width: 30,
                  height: 30,
                  borderRadius: "9px",
                  background: "linear-gradient(135deg,#14b8a6,#0f766e)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "#fff",
                  fontWeight: 800,
                  fontSize: 15,
                  boxShadow: "0 2px 6px rgba(15,118,110,0.45)",
                }}
              >
                ✦
              </Box>
              <Typography variant="h6" sx={{ fontWeight: 800, letterSpacing: "-0.02em" }}>
                Product Assistant Studio
              </Typography>
            </Box>
            {/* No nav/logout up here — the sidebar's user card owns Sign out. */}
          </Toolbar>
        </AppBar>
      )}
      <Container maxWidth={isAdminRoute ? false : "xl"} disableGutters>
        <Box sx={{ minHeight: isAdminRoute && auth ? "calc(100vh - 64px)" : "100vh" }}>
          <Routes>
            <Route path="/" element={<Navigate to="/admin/dashboard/overview" replace />} />
            {/* Entry point for the one-time setup link handed to a merchant
                when their store installs the Shopify app. */}
            <Route path="/set-password" element={<SetPasswordPage onLogin={onLogin} />} />
            {/* The install response advertises this as the admin URL. */}
            <Route path="/login" element={<Navigate to="/admin/dashboard/overview" replace />} />
            <Route path="/admin/chat" element={<Navigate to="/admin/dashboard/overview" replace />} />
            <Route path="/admin/dashboard/*" element={auth ? <AdminDashboard role={auth.role} userEmail={auth.email} onLogout={onLogout} tenantId={auth.tenant_id} tenantIds={auth.tenant_ids || []} /> : <LoginPage onLogin={onLogin} />} />
            <Route path="/admin/dashboard" element={<Navigate to="/admin/dashboard/overview" replace />} />
            <Route path="/admin" element={<Navigate to="/admin/dashboard" replace />} />
          </Routes>
        </Box>
      </Container>
    </>
  );
}

function App() {
  const [auth, setAuth] = useState(null);
  const theme = createTheme({
    palette: {
      mode: "light",
      primary: { main: "#0f766e", light: "#14b8a6", dark: "#115e59", contrastText: "#ffffff" },
      secondary: { main: "#d97706" },
      success: { main: "#16a34a" },
      error: { main: "#dc2626" },
      warning: { main: "#d97706" },
      info: { main: "#0369a1" },
      background: { default: "#f4f9f8", paper: "#ffffff" },
      text: { primary: "#134744", secondary: "#5f7370" },
      divider: "#dbe7e4",
    },
    shape: { borderRadius: 10 },
    typography: {
      fontFamily: '"Inter", "Segoe UI", system-ui, -apple-system, BlinkMacSystemFont, sans-serif',
      h4: { fontWeight: 800, letterSpacing: "-0.02em" },
      h5: { fontWeight: 700, letterSpacing: "-0.01em" },
      h6: { fontWeight: 700, letterSpacing: "-0.01em" },
      subtitle1: { fontWeight: 600 },
      subtitle2: { fontWeight: 600 },
      button: { fontWeight: 600, textTransform: "none" },
    },
    components: {
      MuiCard: {
        defaultProps: { elevation: 0 },
        styleOverrides: {
          root: {
            border: "1px solid #dbe7e4",
            borderRadius: 12,
            boxShadow: "0 1px 2px rgba(19,71,68,0.05)",
          },
        },
      },
      MuiButton: {
        defaultProps: { disableElevation: true },
        styleOverrides: {
          root: { borderRadius: 8, paddingInline: 18, paddingBlock: 7 },
          containedPrimary: {
            color: "#ffffff",
            background: "linear-gradient(180deg,#0d9488 0%,#0f766e 100%)",
            "&:hover": { background: "linear-gradient(180deg,#0f766e 0%,#115e59 100%)" },
            "&.Mui-disabled": { background: "#e0eae8", color: "#93a8a4" },
          },
        },
      },
      MuiAppBar: {
        styleOverrides: {
          root: {
            backgroundColor: "#0c3835",
            color: "#f0fdfa",
            borderBottom: "none",
            boxShadow: "0 1px 8px rgba(12,56,53,0.35)",
          },
        },
      },
      MuiChip: { styleOverrides: { root: { fontWeight: 600, borderRadius: 999 } } },
      MuiOutlinedInput: { styleOverrides: { root: { borderRadius: 8 } } },
      MuiPaper: { styleOverrides: { rounded: { borderRadius: 12 } } },
      MuiTab: { styleOverrides: { root: { textTransform: "none", fontWeight: 600 } } },
      MuiAlert: { styleOverrides: { root: { borderRadius: 10 } } },
      MuiTableCell: { styleOverrides: { head: { fontWeight: 700, color: "#3f5652", backgroundColor: "#eef6f4" } } },
    },
  });

  useEffect(() => {
    if (loadToken()) {
      const cachedRole = localStorage.getItem("role");
      const cachedTenant = localStorage.getItem("tenant_id");
      const cachedTenantIdsRaw = localStorage.getItem("tenant_ids");
      let cachedTenantIds = [];
      if (cachedTenantIdsRaw) {
        try {
          cachedTenantIds = JSON.parse(cachedTenantIdsRaw);
        } catch (err) {
          cachedTenantIds = [];
        }
      }
      if (cachedRole) setAuth({ role: cachedRole, email: localStorage.getItem("email") || "", tenant_id: cachedTenant, tenant_ids: cachedTenantIds });
    }
  }, []);

  const onLogin = (data) => {
    setAuth(data);
    localStorage.setItem("role", data.role);
    localStorage.setItem("email", data.email || "");
    localStorage.setItem("tenant_id", data.tenant_id || "");
    localStorage.setItem("tenant_ids", JSON.stringify(data.tenant_ids || []));
  };

  const onLogout = () => {
    setAuthToken(null);
    setAuth(null);
    localStorage.removeItem("role");
    localStorage.removeItem("email");
    localStorage.removeItem("tenant_id");
    localStorage.removeItem("tenant_ids");
    localStorage.removeItem("admin_dashboard_selected_tenant_id");
  };

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <BrowserRouter>
        <AppContent auth={auth} onLogin={onLogin} onLogout={onLogout} />
      </BrowserRouter>
    </ThemeProvider>
  );
}

export default App; 